import { L1Measurement } from "./L1Measurement"
import { LabelSet } from "./LabelSet"
import { ManualMeasurement } from "./ManualMeasurement"
import { Patient } from "./Patient"
import { PatientNote } from "./PatientNote"
import { User } from "./User"

export enum LabelType {
    NotSpecified = 0,
    HeartMeasurement = 1,
    NeckMeasurement = 2,
    AxillaMeasurement = 3,
    EarMeasurement = 4,
    ComprehensiveMeasurement = 5,
    AutonomousMeasurement = 6,
    Prescription = 7,
    ClinicConversation = 8,
    ClinicConversationChunk = 9,
    ButtonRecording = 10,
    DictationAudio = 11,
    PhoneCallAudio = 12,
    CombinedClinicConv = 13,
    DocumentSoap = 14,
    AudioButtonRecording = 15,
    AggregateLabel = 16,
    NxMIC = 17,
    Placeholder = 18,
    SampleConversation = 19,
    Temperature = 20,
}

export type RecordingTypeInfo = {
    label: string;
    icon: string;
    color: string;
    bgColor: string;
};

export const RECORDING_TYPES: Record<string, RecordingTypeInfo> = {
    'HeartMeasurement': {
        label: 'Heart Measurement',
        icon: 'microchip',
        color: 'text-purple-700',
        bgColor: 'bg-purple-100'
    },
    'NeckMeasurement': {
        label: 'Neck Measurement',
        icon: 'microchip',
        color: 'text-purple-700',
        bgColor: 'bg-purple-100'
    },
    'AxillaMeasurement': {
        label: 'Axilla Measurement',
        icon: 'microchip',
        color: 'text-purple-700',
        bgColor: 'bg-purple-100'
    },
    'EarMeasurement': {
        label: 'Ear Measurement',
        icon: 'microchip',
        color: 'text-purple-700',
        bgColor: 'bg-purple-100'
    },
    'ComprehensiveMeasurement': {  
        label: 'Comprehensive Measurement',
        icon: 'microchip',
        color: 'text-purple-700',
        bgColor: 'bg-purple-100'
    },
    'AutonomousMeasurement': {
        label: 'Autonomous Measurement',
        icon: 'microchip',
        color: 'text-purple-700',
        bgColor: 'bg-purple-100'
    },
    'Prescription': {
        label: 'Prescription',
        icon: 'microchip',
        color: 'text-purple-700',
        bgColor: 'bg-purple-100'
    },
    'ClinicConversation': {
        label: 'Clinic Conversation',
        icon: 'microphone',
        color: 'text-blue-700',
        bgColor: 'bg-blue-100'
    },
    'ButtonRecording': {
        label: 'Button Recording',
        icon: 'microchip',
        color: 'text-purple-700',
        bgColor: 'bg-purple-100'
    },
    'DictationAudio': {
        label: 'Dictation Audio',
        icon: 'microphone',
        color: 'text-blue-700',
        bgColor: 'bg-blue-100'
    },
    'PhoneCallAudio': {
        label: 'Phone Call Audio',
        icon: 'phone',
        color: 'text-orange-700',
        bgColor: 'bg-orange-100'
    },
    'CombinedClinicConv': {
        label: 'Audio Recording',
        icon: 'microphone',
        color: 'text-blue-700',
        bgColor: 'bg-blue-100'
    },
    'DocumentSoap': {
        label: 'Document SOAP',
        icon: 'file-alt',
        color: 'text-green-700',
        bgColor: 'bg-green-100'
    },
    'AudioButtonRecording': {
        label: 'Audio Button Recording',
        icon: 'microphone',
        color: 'text-blue-700',
        bgColor: 'bg-blue-100'
    },
    'AggregateLabel': {
        label: 'Aggregate Label',
        icon: 'file-medical-alt',
        color: 'text-red-700',
        bgColor: 'bg-red-100'
    },
    'NxMIC': {
        label: 'NxMIC',
        icon: 'microphone',
        color: 'text-blue-700',
        bgColor: 'bg-blue-100'
    },
    'Placeholder': {
        label: 'Placeholder',
        icon: 'file-alt',
        color: 'text-gray-700',
        bgColor: 'bg-gray-100'
    },
    'SampleConversation': {
        label: 'Sample Conversation',
        icon: 'microphone',
        color: 'text-blue-700',
        bgColor: 'bg-blue-100'
    },
    'Temperature': {
        label: 'Temperature',
        icon: 'thermometer',
        color: 'text-red-700',
        bgColor: 'bg-red-100'
    },
};

export class Label {
    id: string = ""
    name: string = ""
    fromTime: number = 0
    toTime?: number = 0
    color: string | null = null
    description: string = ""
    labelSetId: string = ""
    labelSet: LabelSet | null = null
    patientId: string = ""
    patient: Patient | null = null
    type: keyof typeof LabelType  = "NotSpecified"
    l1Measurements: L1Measurement[] | null = null
    ownedPatientNotes: PatientNote[] | null = null
    createdBy: User | null = null
    medicalTemplateId?: string = undefined
    metadata?: Record<string, any> = {}
    manualMeasurements: ManualMeasurement[] | null = null
    isCreatedByOnboarding?: boolean = false
    deviceSerial?: string = undefined
    friendlyName?: string | null = undefined
    aggregatedFromLabels?: Label[] | null = null

    constructor(init?: Partial<Label & { metadata: string | Record<string, any> }>) {
        if (!init) {
            return
        }

        Object.assign(this, init);

        if (init.labelSet) {
            this.labelSet = new LabelSet(init.labelSet)
        }

        if (init.patient) {
            this.patient = new Patient(init.patient)
        }

        if (init.l1Measurements) {
            this.l1Measurements = init.l1Measurements.map(measurement => new L1Measurement(measurement));
        }

        if (init.ownedPatientNotes) {
            this.ownedPatientNotes = init.ownedPatientNotes.map((note: PatientNote) => new PatientNote(note))
        }

        if (init.createdBy) {
            this.createdBy = new User(init.createdBy);
        }

        if (init.manualMeasurements) {
            this.manualMeasurements = init.manualMeasurements.map(measurement => new ManualMeasurement(measurement));
        }

        if (init.metadata) {
            let metadata;
            if (typeof init.metadata === 'string') {
                metadata = init.metadata[0] === '"' ? JSON.parse(init.metadata) : init.metadata;
                this.metadata = typeof metadata === 'string' ? JSON.parse(metadata) : metadata;
            } else {
                this.metadata = init.metadata;
            }
        }

        if (init.aggregatedFromLabels) {
            this.aggregatedFromLabels = init.aggregatedFromLabels.map(label => new Label(label));
        }
    }

    toDevicePageUrl(): string {
        return `/devices/${this.labelSet?.device?.id}`;
    }

    toDataPageUrl(): string {
        const searchParam = [];
        if (this.fromTime) {
            searchParam.push(`fromTime=${+this.fromTime}`);
        }
        if (this.toTime) {
            searchParam.push(`toTime=${+this.toTime}`);
        }
        return `/devices/${this.labelSet?.device?.serial}/labels/${this.id}/data?latestSec=14400&${searchParam.join('&')}`;
    }

    toDetailPageUrl(): string {
        const searchParam = [];
        if (this.fromTime) {
            searchParam.push(`fromTime=${+this.fromTime}`);
        }
        if (this.toTime) {
            searchParam.push(`toTime=${+this.toTime}`);
        }
        if (this.type === "ClinicConversation" || this.type === "DictationAudio" || this.type === "PhoneCallAudio" || this.type === "CombinedClinicConv" || this.type === "AudioButtonRecording" || this.type === "NxMIC") {
            searchParam.push(`channel=AudioRecording`);
        }
        if (this.type === "DocumentSoap") {
            searchParam.push(`channel=Document`);
        }
        return `/labels/${this.id}/detail?latestSec=14400&${searchParam.join('&')}`;
    }


    get soapSummary(): string | undefined {
        try {
            // Parse metadata if it's a string, otherwise use as-is
            let parsedMetadata: any;
            if (typeof this.metadata === 'string') {
                parsedMetadata = JSON.parse(this.metadata);
            } else {
                parsedMetadata = this.metadata;
            }

            // Access petData from the parsed metadata
            const petData = parsedMetadata?.petData;
            const animalName = petData?.animal_name?.value;
            const animalNameExists = petData?.animal_name?.exists;
            const summary = petData?.summary?.value;
            
            // Only show animalName if the patient is a default patient and animalName exists and is not null/empty
            const isDefaultPatient = this.patient?.isDefault;
            const hasValidAnimalName = animalNameExists && animalName && animalName.trim() !== '';
            return (isDefaultPatient && hasValidAnimalName) ? `${animalName}: ${summary}` : summary;
        } catch (error) {
            console.error("Error parsing SOAP note content:", error);
            return undefined;
        }
    }
}
